import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import {
  useDataEntry,
  useContainerImageByParams,
  useQuoteDocument,
  useDraftBlDocument,
  useCopyDocuments,
} from '../../queries/dataEntryQueries';

// ── Container Card Component for OTHER DOCUMENT Tab ──────────────────────────
const ContainerOtherDocCard = ({ container, fileName, blNo, onOpenModal }) => {
  const { data: imageResponse } = useContainerImageByParams(
    fileName,
    blNo,
    container?.containerNo
  );

  const containerImagesList =
    Array.isArray(imageResponse) && imageResponse.length > 0
      ? imageResponse
      : Array.isArray(imageResponse?.containerImage)
      ? imageResponse.containerImage
      : Array.isArray(container?.containerImage)
      ? container.containerImage
      : [];

  const firstImg = containerImagesList[0];
  const imgSrc = typeof firstImg === 'string'
    ? (firstImg.startsWith('data:image') ? firstImg : `data:image/jpeg;base64,${firstImg}`)
    : typeof firstImg === 'object' && firstImg?.base64
    ? (firstImg.base64.startsWith('data:image') ? firstImg.base64 : `data:image/jpeg;base64,${firstImg.base64}`)
    : typeof firstImg === 'object' && firstImg?.url
    ? firstImg.url
    : null;

  return (
    <div className="col-12 col-md-6 col-xl-4">
      <div
        className="card h-100 shadow-sm border rounded-3 overflow-hidden d-flex flex-column"
        style={{ cursor: 'pointer' }}
        onClick={() => onOpenModal(container)}
      >
        {/* Preview area */}
        <div
          className="bg-light p-2 text-center border-bottom d-flex flex-column align-items-center justify-content-center overflow-hidden position-relative"
          style={{ height: '140px' }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={container?.containerNo}
              className="w-100 h-100 rounded"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <div className="text-muted small">
              <span className="fs-2">🚢</span>
              <div className="mt-1 fw-semibold text-secondary">{container?.containerNo}</div>
              <span className="badge bg-secondary-subtle text-secondary border mt-1">No Image</span>
            </div>
          )}
        </div>

        {/* Footer matching reference: CON : {containerNo}, GIRTH : {avgCftAvgGirth} */}
        <div className="gtss-bg-primary text-white p-2 d-flex align-items-center justify-content-between mt-auto">
          <span className="fw-semibold text-truncate" style={{ fontSize: '11.5px' }}>
            CON : {container?.containerNo || 'N/A'}, GIRTH : {container?.avgCftAvgGirth || '-'}
          </span>
          {containerImagesList.length > 0 && (
            <span className="badge bg-light text-primary fw-bold ms-1" style={{ fontSize: '10px' }}>
              📷 {containerImagesList.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * FileDocumentPage — Container List & Document Files screen.
 *
 * Route: /business/file-document/:id
 * Active Step in Timeline: 4 (FILE DOCUMENT)
 */
const FileDocumentPage = () => {
  const { id } = useParams();
  const { data: retrievedData, isLoading, isError } = useDataEntry(id);

  // Local state
  const [activeTab, setActiveTab] = useState('containerList'); // 'containerList' | 'otherDocument'
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch Container Images via GET /api/data-entry/container-image?fileName=...&blNo=...&containerNo=...
  const { data: fetchedContainerImage } = useContainerImageByParams(
    retrievedData?.fileName,
    retrievedData?.blNo || retrievedData?.billno,
    selectedContainer?.containerNo
  );


  // Extract containers array safely
  const containers = Array.isArray(retrievedData?.userForm) && retrievedData.userForm.length > 0
    ? retrievedData.userForm
    : Array.isArray(retrievedData?.dataEntryForm) && retrievedData.dataEntryForm.length > 0
    ? retrievedData.dataEntryForm
    : [];

  // ── Document file GET APIs ─────────────────────────────────────────────────
  // These are separate endpoints from the main data entry — must be called explicitly.

  const fileName = retrievedData?.fileName || '';
  const blNo     = retrievedData?.blNo || retrievedData?.billno || '';

  const { data: quoteDocData,   isLoading: loadingQuote   } = useQuoteDocument(fileName, blNo);
  const { data: draftBlDocData, isLoading: loadingDraftBl } = useDraftBlDocument(fileName, blNo);
  const { data: copyDocsData,   isLoading: loadingCopy    } = useCopyDocuments(fileName, blNo);

  const isDocsLoading = loadingQuote || loadingDraftBl || loadingCopy;

  /**
   * Build otherDocs from API responses.
   * Each entry: { label, fileName, base64 }
   *   - label    : display name shown on the card
   *   - fileName : filename returned by API (shown as badge)
   *   - base64   : raw base64 string if API returns file content (used for PDF preview)
   */
  const resolveDoc = (apiResponse) => {
    if (!apiResponse) return null;
    // API may return a single object or an array; normalise to object
    const obj = Array.isArray(apiResponse) ? apiResponse[0] : apiResponse;
    if (!obj) return null;
    return {
      fileName: obj.fileName || obj.name || null,
      base64:   obj.base64  || obj.fileContent || obj.content || null,
      url:      obj.url     || null,
    };
  };

  const quoteDoc   = resolveDoc(quoteDocData);
  const draftBlDoc = resolveDoc(draftBlDocData);

  // Copy documents can be multiple
  const copyDocs = Array.isArray(copyDocsData)
    ? copyDocsData.map((doc, idx) => resolveDoc(doc) || { fileName: `Copy Doc ${idx + 1}`, base64: null })
    : copyDocsData ? [resolveDoc(copyDocsData)].filter(Boolean) : [];

  /**
   * otherDocs — final list for the OTHER DOCUMENT tab.
   * Only includes documents where API returned a response (not null).
   */
  const otherDocs = [
    quoteDoc   ? { label: 'QUOTE DOCUMENT',    ...quoteDoc   } : null,
    draftBlDoc ? { label: 'DRAFT BL DOCUMENT', ...draftBlDoc } : null,
    ...copyDocs.map((doc, idx) => ({
      label: `COPY DOCUMENT ${copyDocs.length > 1 ? idx + 1 : ''}`.trim(),
      ...doc,
    })),
  ].filter(Boolean);

  /**
   * openImageInNewTab — Opens an image in a new browser tab.
   * Chrome blocks direct navigation to data: URLs (blank page bug).
   * Fix: convert base64 → Blob → object URL, then window.open() that.
   */
  const openImageInNewTab = (imgSrc) => {
    if (!imgSrc) return;
    // Regular / blob URL — open directly
    if (imgSrc.startsWith('blob:') || imgSrc.startsWith('http')) {
      window.open(imgSrc, '_blank', 'noreferrer');
      return;
    }
    // data:image/... URL — convert to Blob first to satisfy Chrome's policy
    try {
      const [header, base64Data] = imgSrc.split(',');
      const mimeType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
      const byteCharacters = atob(base64Data);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noreferrer');
      // Revoke after 60 s — enough time for the new tab to load the image
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('openImageInNewTab: could not open image', err);
    }
  };

  // PDF Generator for Container Images & Info
  const downloadContainerPdf = (containerData) => {
    if (!containerData) return;

    // Resolve images from the GET API response (same source the modal uses)
    const containerImagesList =
      Array.isArray(fetchedContainerImage) && fetchedContainerImage.length > 0
        ? fetchedContainerImage
        : Array.isArray(fetchedContainerImage?.containerImage)
        ? fetchedContainerImage.containerImage
        : Array.isArray(containerData?.containerImage)
        ? containerData.containerImage
        : [];

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header Banner
      doc.setFillColor(26, 86, 219); // GTSS Primary
      doc.rect(0, 0, pageWidth, 24, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('CONTAINER SUMMARY REPORT', pageWidth / 2, 15, { align: 'center' });

      // Details
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`File Name: ${retrievedData?.fileName || '-'}`, 14, 34);
      doc.text(`Container Name: ${containerData?.containerNo || 'N/A'}`, 14, 42);
      doc.text(`Average Length: ${containerData?.averageLength || 'N/A'}`, 14, 50);
      doc.text(`Avg CFT / Girth: ${containerData?.avgCftAvgGirth || 'N/A'}`, 14, 58);
      doc.text(`Pieces: ${containerData?.pcs || '0'}`, 14, 66);

      // Section header for images
      let yPos = 76;

      if (containerImagesList.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(26, 86, 219);
        doc.text('Container Images', 14, yPos);
        doc.setDrawColor(26, 86, 219);
        doc.line(14, yPos + 2, pageWidth - 14, yPos + 2);
        yPos += 10;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);

        containerImagesList.forEach((img, index) => {
          // Resolve image source — API returns base64 string or object with base64/url
          let imgSrc = '';
          if (typeof img === 'string') {
            imgSrc = img.startsWith('data:image') ? img : `data:image/jpeg;base64,${img}`;
          } else if (typeof img === 'object') {
            const raw = img?.base64 || img?.url || img?.fileName || '';
            imgSrc = raw.startsWith('data:image') ? raw : raw ? `data:image/jpeg;base64,${raw}` : '';
          }

          if (!imgSrc) return;

          const imgWidth = 140;
          const imgHeight = 90;
          const xPos = (pageWidth - imgWidth) / 2;

          // Add new page if not enough space
          if (yPos + imgHeight + 15 > 285) {
            doc.addPage();
            yPos = 20;
          }

          try {
            doc.addImage(imgSrc, 'JPEG', xPos, yPos, imgWidth, imgHeight);
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`Image ${index + 1}`, pageWidth / 2, yPos + imgHeight + 6, { align: 'center' });
            yPos += imgHeight + 16;
          } catch (err) {
            console.error(`Error adding image ${index + 1} to PDF:`, err);
          }
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(130, 130, 130);
        doc.text('No images uploaded for this container.', 14, yPos);
      }

      doc.save(`Container_${containerData?.containerNo || 'Details'}.pdf`);
    } catch (e) {
      console.error('Error generating container PDF:', e);
      alert('Could not generate container PDF.');
    }
  };


  // WhatsApp Share Handler
  const handleShareWhatsapp = (containerData) => {
    const containerNo = containerData?.containerNo || 'N/A';
    const avgCft = containerData?.avgCftAvgGirth || 'N/A';
    const pcs = containerData?.pcs || '0';
    const fileName = retrievedData?.fileName || '';

    const message = `*Container Details*\nFile: ${fileName}\nContainer No: ${containerNo}\nAvg CFT/Girth: ${avgCft}\nPieces: ${pcs}`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // View Container Modal Action
  const handleOpenContainerModal = (containerData) => {
    setSelectedContainer(containerData);
    setIsModalOpen(true);
  };

  // Base64 helper for PDF Blob URL
  const getPdfBlobUrl = (base64Str) => {
    if (!base64Str) return null;
    const cleanBase64 = base64Str.replace(/^data:application\/pdf;base64,/, '');
    try {
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (e) {
      return null;
    }
  };

  return (
    <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
      <div
        className="d-flex flex-column flex-grow-1 mx-auto w-100"
        style={{ maxWidth: '1280px' }}
      >
        {/* ── Step Progress Timeline — Step 5 active (FILE DOCUMENT) ── */}
        <div className="flex-shrink-0 pb-1">
          <DeTimeline activeStep={5} />
        </div>

        {/* ── Bootstrap 5 two-column layout ── */}
        <div className="row g-3 flex-grow-1 overflow-hidden">
          {/* ── Main Content Area (Left Column) ── */}
          <div className="col-12 col-lg-9 d-flex flex-column align-items-center">
            {isLoading ? (
              <div className="card shadow-sm w-100 p-5 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading file document data...
              </div>
            ) : isError ? (
              <div className="card shadow-sm w-100 p-5 text-center text-danger">
                Failed to load document data. Please try again.
              </div>
            ) : (
              <div className="w-100 d-flex flex-column align-items-center">
                {/* ── Document Container Card ── */}
                <div className="eway-container bg-white border rounded-3 p-2 p-sm-4 shadow-sm w-100 d-flex flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
                  {/* Header & Tab Switcher Bar */}
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3 border-bottom pb-2">
                    <h5 className="fw-bold text-uppercase text-primary mb-0">
                      {activeTab === 'containerList' ? 'CONTAINER LIST' : 'OTHER DOCUMENT'}
                    </h5>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary fw-bold text-uppercase px-3 d-flex align-items-center gap-2"
                      onClick={() =>
                        setActiveTab((prev) => (prev === 'containerList' ? 'otherDocument' : 'containerList'))
                      }
                    >
                      <span>{activeTab === 'containerList' ? 'OTHER DOCUMENT' : 'CONTAINER LIST'}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zM2.5 10a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5z"/>
                      </svg>
                    </button>
                  </div>

                  {/* ── TAB 1: CONTAINER LIST ── */}
                  {activeTab === 'containerList' && (
                    <div>
                      {/* File Name Banner */}
                      <div className="mb-3 d-flex align-items-center gap-2">
                        <span className="eway-meta-label">FILE NAME :</span>
                        <span className="eway-meta-value font-monospace fs-6">{retrievedData?.fileName || '-'}</span>
                      </div>

                      {/* Container Table */}
                      <div className="table-responsive border rounded-2 mb-2" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <table className="table table-bordered align-middle text-center mb-0 eway-table text-nowrap" style={{ minWidth: '315px' }}>
                          <thead className="gtss-bg-primary text-white text-uppercase">
                            <tr>
                              <th className="text-center py-2 px-1" style={{ width: '35px' }}>S.NO</th>
                              <th className="text-center py-2 px-1">
                                CONTAINER<br className="d-sm-none" /> NAME
                              </th>
                              <th className="text-center py-2 px-1">
                                AVG<br className="d-sm-none" /> LEN
                              </th>
                              <th className="text-center py-2 px-1">
                                AVG CFT /<br className="d-sm-none" /> GIRTH
                              </th>
                              <th className="text-center py-2 px-1">
                                PICES
                              </th>
                              <th className="text-center py-2 px-1" style={{ width: '55px' }}>
                                SHARE
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {containers.length > 0 ? (
                              containers.map((data, index) => (
                                <tr key={index}>
                                  <td className="text-center py-1.5 px-1 fw-semibold">{index + 1}</td>
                                  <td className="text-center py-1.5 px-1 font-monospace fw-bold text-primary text-nowrap">
                                    {data?.containerNo || '-'}
                                  </td>
                                  <td className="text-center py-1.5 px-1 font-monospace fw-semibold">{data?.averageLength || '-'}</td>
                                  <td className="text-center py-1.5 px-1 font-monospace fw-semibold">{data?.avgCftAvgGirth || '-'}</td>
                                  <td className="text-center py-1.5 px-1 font-monospace fw-semibold">{data?.pcs || 0}</td>
                                  <td className="text-center py-1.5 px-1">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      {/* WhatsApp Share Button */}
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-success border-0 p-0.5"
                                        title="Share via WhatsApp"
                                        onClick={() => handleShareWhatsapp(data)}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                          <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592z"/>
                                        </svg>
                                      </button>

                                      {/* View / Download Modal Button */}
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary border-0 p-0.5"
                                        title="View Container Details"
                                        onClick={() => handleOpenContainerModal(data)}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.173 8a13 13 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5s3.879 1.168 5.168 2.457A13 13 0 0 1 14.828 8q-.086.13-.195.288c-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5s-3.879-1.168-5.168-2.457A13 13 0 0 1 1.172 8z"/>
                                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0"/>
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">
                                  No containers found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}



                  {/* ── TAB 2: OTHER DOCUMENT ── */}
                  {activeTab === 'otherDocument' && (
                    <div>
                      {/* Loading state while document APIs fetch */}
                      {isDocsLoading ? (
                        <div className="py-5 text-center text-muted">
                          <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                          Loading documents...
                        </div>
                      ) : otherDocs.length === 0 && containers.length === 0 ? (
                        <div className="py-5 text-center text-muted">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="mb-3 opacity-50" viewBox="0 0 16 16">
                            <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                          </svg>
                          <p className="mb-0">No documents or containers available.</p>
                          <small>Upload documents in the Document Upload section first.</small>
                        </div>
                      ) : (
                        <div className="row g-3">
                          {/* Document Cards */}
                          {otherDocs.map((item, index) => {
                            const blobUrl = item.base64 ? getPdfBlobUrl(item.base64) : null;

                            return (
                              <div key={index} className="col-12 col-md-6 col-xl-4">
                                <div className="card h-100 shadow-sm border rounded-3 overflow-hidden d-flex flex-column">

                                  {/* Preview area */}
                                  <div
                                    className="bg-light p-3 text-center border-bottom d-flex flex-column align-items-center justify-content-center"
                                    style={{ height: '140px' }}
                                  >
                                    {blobUrl ? (
                                      <iframe
                                        title={item.label}
                                        src={`${blobUrl}#toolbar=0`}
                                        className="w-100 h-100 border-0"
                                        style={{ pointerEvents: 'none' }}
                                      />
                                    ) : (
                                      <div className="text-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="var(--gtss-primary)" className="mb-2 opacity-75" viewBox="0 0 16 16">
                                          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                                          <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029z"/>
                                        </svg>
                                        {item.fileName && (
                                          <div className="text-truncate small text-muted fw-semibold px-2" style={{ maxWidth: '160px' }}>
                                            📄 {item.fileName}
                                          </div>
                                        )}
                                        <span className="badge bg-success-subtle text-success border border-success-subtle mt-1 small">
                                          ✅ Uploaded
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Card footer */}
                                  <div className="gtss-bg-primary text-white p-2 d-flex align-items-center justify-content-between mt-auto">
                                    <div className="d-flex flex-column" style={{ maxWidth: 'calc(100% - 70px)' }}>
                                      <span className="fw-semibold text-truncate" style={{ fontSize: '11.5px' }}>
                                        {item.label}
                                      </span>
                                      {item.fileName && !blobUrl && (
                                        <span className="text-truncate opacity-75" style={{ fontSize: '10px' }}>
                                          {item.fileName}
                                        </span>
                                      )}
                                    </div>
                                    {blobUrl && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light text-primary fw-bold p-1 px-2 flex-shrink-0"
                                        style={{ fontSize: '11px' }}
                                        onClick={() => window.open(blobUrl, '_blank')}
                                      >
                                        📥 View
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Container Image Cards (Matching reference layout: CON : {containerNo}, GIRTH : {avgCftAvgGirth}) */}
                          {containers.map((container, cIdx) => (
                            <ContainerOtherDocCard
                              key={container?.containerNo || cIdx}
                              container={container}
                              fileName={fileName}
                              blNo={blNo}
                              onOpenModal={handleOpenContainerModal}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            )}
          </div>

          {/* ── Right Sidebar Column ── */}
          <div className="col-12 col-lg-3">
            <DeRightSidebar id={id} activeStep={5} />
          </div>
        </div>
      </div>

      {/* ── Container Detail View Modal ── */}
      {isModalOpen && selectedContainer && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header gtss-bg-primary text-white">
                <h5 className="modal-title fw-bold">
                  Container: {selectedContainer?.containerNo || 'Details'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setIsModalOpen(false)}
                />
              </div>
              <div className="modal-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-6 col-md-3">
                    <span className="eway-meta-label d-block">AVG LEN</span>
                    <span className="eway-meta-value font-monospace fs-6">{selectedContainer?.averageLength || '-'}</span>
                  </div>
                  <div className="col-6 col-md-3">
                    <span className="eway-meta-label d-block">AVG CFT/GIRTH</span>
                    <span className="eway-meta-value font-monospace fs-6">{selectedContainer?.avgCftAvgGirth || '-'}</span>
                  </div>
                  <div className="col-6 col-md-3">
                    <span className="eway-meta-label d-block">PICES</span>
                    <span className="eway-meta-value font-monospace fs-6">{selectedContainer?.pcs || '0'}</span>
                  </div>
                </div>

                {/* Container Images */}
                <h6 className="fw-bold text-uppercase text-primary border-bottom pb-2 mb-3">Container Images</h6>
                {(() => {
                  const containerImagesList = Array.isArray(fetchedContainerImage) && fetchedContainerImage.length > 0
                    ? fetchedContainerImage
                    : Array.isArray(fetchedContainerImage?.containerImage)
                    ? fetchedContainerImage.containerImage
                    : Array.isArray(selectedContainer?.containerImage)
                    ? selectedContainer.containerImage
                    : [];

                  return containerImagesList.length > 0 ? (
                    <div className="row g-3">
                      {containerImagesList.map((img, idx) => {
                        const imgSrc = typeof img === 'string'
                          ? img.startsWith('data:image') ? img : `data:image/jpeg;base64,${img}`
                          : typeof img === 'object' && img?.base64
                          ? img.base64.startsWith('data:image') ? img.base64 : `data:image/jpeg;base64,${img.base64}`
                          : '';

                        return (
                          <div key={idx} className="col-6 col-md-4">
                            <div className="border rounded p-1 bg-light text-center">
                              <img
                                src={imgSrc}
                                alt={`Container ${idx + 1}`}
                                className="img-fluid rounded"
                                style={{ maxHeight: '180px', objectFit: 'cover', cursor: 'pointer' }}
                                onClick={() => openImageInNewTab(imgSrc)}
                                title="Click to open full image"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3">No images available for this container.</p>
                  );
                })()}

              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Close
                </button>
                {/* <button
                  type="button"
                  className="btn btn-orange fw-bold"
                  onClick={() => downloadContainerPdf(selectedContainer)}
                >
                  📥 Download Container PDF
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileDocumentPage;
